<template>
  <div
    class="p-2 py-4 flex gap-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md select-none relative"
  >
    <!--    <IconBookmark class="flex-shrink-0" />-->
    <div class="min-w-0">
      <p class="font-bold pr-8" v-if="!noName">{{ chapterName }}</p>
      <div>
        <p
          :class="{
            '!line-clamp-2 pr-8': noName,
            truncate: !noName,
          }"
        >
          <component
            v-if="typeToIconMap[bookmarkBlockType] && isBookmark"
            :is="typeToIconMap[bookmarkBlockType]"
            class="inline-block mr-1 mb-0.5"
            size="1rem"
          />
          <span
            :class="{
              'bg-yellow-300/50 dark:bg-yellow-700/50':
                bookmark.attributes.position !== BOOKMARK_POSITION,
            }"
          >
            {{ textValue }}
          </span>
        </p>
        <p
          v-if="bookmark.attributes.comment"
          class="text-lg font-roboto mt-2 -mb-1"
        >
          {{ bookmark.attributes.comment }}
        </p>
        <p v-if="bookmark.attributes.position !== BOOKMARK_POSITION">
          <span class="text-xs text-neutral-500 dark:text-neutral-400 italic">
            {{ dateFormatter(new Date(bookmark.attributes.createdAt)) }}
          </span>
        </p>
      </div>
    </div>

    <ContextMenuListNew
      v-if="!noOptions"
      class="absolute right-1 top-1"
      :options="<Option[]>[
          isBookmark
            ? null
            : {
                value: 'edit',
                text: t(key('options.edit')),
                type: 'default',
                action: () => $emit('edit'),
                icon: IconPencil,
              },
          {
            value: 'delete',
            text: t(key('options.delete')),
            type: 'danger',
            action: () => $emit('delete'),
            icon: IconTrash,
          },
        ].filter(onlyTruthys)"
    >
      <template #default="{ toggle }">
        <button
          class="rounded-full w-8 h-8 flex justify-center items-center hover:bg-neutral-300 dark:hover:bg-neutral-700 transition-colors"
          @click="
            (e) => {
              e.stopPropagation();
              toggle();
            }
          "
        >
          <IconDots size="1.25rem" />
        </button>
      </template>
    </ContextMenuListNew>
  </div>
</template>

<script setup lang="ts">
import {
  BOOKMARK_POSITION,
  isExpanded,
  isNovelChapter,
  isNovelParagraph,
  type NovelBookmarkEntity,
  type NovelParagraphEntity,
  type NovelParagraphRelation,
  type PopulateRelationship,
} from "~/src/api";
import {
  IconDots,
  IconPhoto,
  IconPencil,
  IconTrash,
  IconBlockquote,
  IconInfoCircle,
  IconPerspective,
} from "@tabler/icons-vue";
import { render } from "~/src/novel/render";
import type { TextBlock } from "~/src/novel/core";
import type { Option } from "~/components/contextMenu/ContextMenuListNew.vue";

type BlockType = "text" | "blockquote" | "callout" | "verse" | "image";

const { bookmark } = defineProps<{
  bookmark: NovelBookmarkEntity;
  noName?: boolean;
  noOptions?: boolean;
}>();

defineEmits<{
  (e: "edit"): void;
  (e: "delete"): void;
}>();

const typeToIconMap: Record<BlockType, null | typeof IconBlockquote> = {
  text: null,
  blockquote: IconBlockquote,
  callout: IconInfoCircle,
  verse: IconPerspective,
  image: IconPhoto,
};

const { locale, t } = useI18n();
const key = useI18nKey("components.novel.bookmark.novelBookmarkCard");
const dateFormatter = useDateFormatter(locale);

const isBookmark = computed(() => {
  return bookmark.attributes.position === BOOKMARK_POSITION;
});

const paragraph = computed(() => {
  const found = bookmark.relationships.find(isNovelParagraph);
  if (found && isExpanded(found)) return found;
  return null;
});

const chapter = computed(() => {
  const found = bookmark.relationships.find(isNovelChapter);
  if (found && isExpanded(found)) return found;
  return null;
});

const chapterName = computed(() => {
  if (!chapter.value) return "";
  const { chapter: chapterChapter, volume, name } = chapter.value.attributes;
  return createChapterName(chapterChapter, volume, name, "short");
});

const textValue = computed(() => {
  if (!paragraph.value) return "";
  const text = getTextRepresentationOfParagraph(paragraph.value);
  return getSubstringFromBookmarkPosition(text, bookmark.attributes.position);
});

const bookmarkBlockType = computed<BlockType>(() => {
  if (!paragraph.value) return "text";

  switch (paragraph.value.attributes.text.type) {
    case "image":
    case "blockquote":
    case "callout":
    case "verse":
      return paragraph.value.attributes.text.type;
    default:
      return "text";
  }
});

function getTextRepresentationOfParagraph(
  paragraph:
    | NovelParagraphEntity
    | PopulateRelationship<NovelParagraphRelation>,
) {
  const json = paragraph.attributes.text;
  if (json.type === "image") return getTextRepresentationOfImageTextBlock(json);
  return getTextRepresentationOfRenderedTextBlock(json);
}

function getTextRepresentationOfImageTextBlock(textBlock: TextBlock) {
  if (textBlock.type !== "image") return "";
  if (typeof textBlock.content === "string") return textBlock.content;
  const node = render(textBlock.content);
  return node.at(0)?.getAttribute("alt") ?? t(key("image_no_alt_text"));
}

function getTextRepresentationOfRenderedTextBlock(textBlock: TextBlock) {
  if (typeof textBlock.content === "string") return textBlock.content;
  const nodes = render(textBlock.content);
  return nodes.map((n) => n.textContent).join("");
}

function getOffsetAndLengthFromBookmarkPosition(position: string) {
  const [offset, length] = position.split(";").map((s) => parseInt(s));
  return { offset, length };
}

function getSubstringFromBookmarkPosition(text: string, position: string) {
  if (position === BOOKMARK_POSITION) return text;
  const { offset, length } = getOffsetAndLengthFromBookmarkPosition(position);
  return text.substring(offset, offset + length);
}
</script>

<style scoped lang="postcss"></style>
